import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedActions from '../components/FixedActions';
import { ArrowRight, Target, Award, Users, Zap } from 'lucide-react';

const Sports = () => {
  const sportsServices = [
    {
      name: 'Pickleball Courts',
      slug: 'pickleball-courts',
      description: 'Regulation pickleball courts with proper court dimensions, net systems, and surface materials for competitive play.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/gp32w7yp_Pickleball%20Court%20display.jpg',
      features: ['Regulation Court Size', 'Non-Volley Zones', 'Professional Nets', 'Non-Slip Surface']
    },
    {
      name: 'Football Turf',
      slug: 'football-turf',
      description: 'FIFA-approved artificial turf and natural grass fields with proper drainage and shock absorption systems.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/kjsxdtwc_Football%20turf%20%20display%20.jpg',
      features: ['FIFA Approved Turf', 'Drainage System', 'Goal Posts', 'Shock Absorption']
    },
    {
      name: 'Cricket Turf',
      slug: 'cricket-turf',
      description: 'Professional cricket pitches with natural and artificial turf options, proper drainage, and boundary specifications.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/v6hwxmwi_Cricket%20turf%20display%20.jpg',
      features: ['Natural/Artificial Turf', 'Pitch Preparation', 'Boundary Rope', 'Drainage System']
    },
    {
      name: 'Padel Courts',
      slug: 'padel-courts',
      description: 'Professional padel courts with glass walls, artificial grass surface, and regulation dimensions for competitive play.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/e2lm5uhx_Padel%20court%20display%20%20%281%29.jpg',
      features: ['Glass Walls', 'Artificial Grass', 'LED Lighting', 'Regulation Size']
    },
    {
      name: 'Badminton Courts',
      slug: 'badminton-courts',
      description: 'Professional badminton courts with synthetic flooring, LED lighting, and regulation-compliant dimensions for optimal gameplay.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/fok2sels_Badminton%20Court%20display.jpg',
      features: ['Synthetic Flooring', 'LED Lighting', 'Professional Nets', 'Sound Absorption']
    },
    {
      name: 'Athlete Track',
      slug: 'athlete-track',
      description: 'Professional athletic running tracks with IAAF-standard measurements, lane markings, and all-weather surfaces.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/yer03c04_Athlete%20Track%20display%20.jpg',
      features: ['IAAF Standards', 'All-Weather Surface', 'Lane Markings', 'Starting Blocks']
    },
    {
      name: 'Basketball Courts',
      slug: 'basketball-courts',
      description: 'NBA-regulation basketball courts with hardwood flooring, tempered glass backboards, and professional hoops.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/rtqw6pp0_Basketball%20court%20display.jpg', 
      features: ['Hardwood Flooring', 'Glass Backboards', 'Adjustable Hoops', 'Court Markings']
    },
    {
      name: 'Volleyball Courts',
      slug: 'volleyball-courts',
      description: 'Indoor and outdoor volleyball courts with regulation net height, proper flooring, and boundary markings.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/k978dxgk_Volleyballcourt%20display%20.jpg',
      features: ['Regulation Net Height', 'Attack Lines', 'Sand/Synthetic Surface', 'Boundary Markings']
    },
    {
      name: 'Squash Courts',
      slug: 'squash-courts',
      description: 'Indoor squash courts with regulation dimensions, front wall specifications, and proper ventilation systems.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/66o4v4t6_Squash%20court%20display%20.jpg',
      features: ['Regulation Dimensions', 'Glass Back Wall', 'Hardwood Flooring', 'Ventilation System']
    },
    {
      name: 'Tennis Courts',
      slug: 'tennis-courts', 
      description: 'ITF-standard tennis courts with various surface options including hard court, clay, and grass surfaces.',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/jvp43j3j_Tennis%20court%20display.jpg',
      features: ['Hard Court Surface', 'Clay Options', 'Professional Lighting', 'Net Systems']
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <Header />
      
      {/* Hero Section */}
      <section style={{ 
        padding: '120px 0 60px', 
        background: 'linear-gradient(135deg, #004534 0%, #006B4A 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200") center/cover',
          opacity: 0.1
        }} />
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ 
            textAlign: 'center', 
            maxWidth: '800px', 
            margin: '0 auto',
            color: 'white'
          }}>
            <h1 style={{ 
              fontSize: '3.5rem', 
              fontWeight: 700, 
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              Professional Sports Court Construction
            </h1>
            <p style={{ 
              fontSize: '1.25rem', 
              marginBottom: '40px',
              opacity: 0.9,
              lineHeight: 1.6
            }}>
              Building world-class sports facilities with precision engineering, premium materials, 
              and attention to every detail that matters for optimal performance.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section style={{ padding: '80px 0', background: '#FAFFEE' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 700, 
              color: 'var(--brand-dark)',
              marginBottom: '16px'
            }}>
              Our Sports Court Services
            </h2>
            <p style={{ 
              fontSize: '1.125rem', 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Specialized construction services for various sports facilities, 
              built to professional standards and regulations.
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '40px'
          }}>
            {sportsServices.map((service, index) => (
              <div key={index} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
              }}>
                <div style={{ 
                  width: '100%',
                  aspectRatio: '3/2',
                  maxHeight: '250px',
                  backgroundImage: `url(${service.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    background: 'var(--brand-primary)',
                    color: 'var(--brand-dark)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}>
                    Professional Grade
                  </div>
                </div>
                
                <div style={{ padding: '30px' }}>
                  <h3 style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 700, 
                    color: 'var(--brand-dark)',
                    marginBottom: '12px'
                  }}>
                    {service.name}
                  </h3>
                  
                  <p style={{ 
                    color: 'var(--text-secondary)', 
                    lineHeight: 1.6,
                    marginBottom: '20px'
                  }}>
                    {service.description}
                  </p>
                  
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: 600,
                      color: 'var(--brand-dark)',
                      marginBottom: '8px'
                    }}>
                      Key Features:
                    </h4>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '8px'
                    }}>
                      {service.features.map((feature, idx) => (
                        <span key={idx} style={{
                          background: 'rgba(211, 255, 98, 0.2)',
                          color: 'var(--brand-dark)',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }}>
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <Link 
                    to={`/sports/${service.slug}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'var(--brand-dark)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '25px',
                      textDecoration: 'none',
                      fontWeight: 600,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#003429';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--brand-dark)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    View Projects <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        @media (max-width: 1024px) {
          div[style*="grid-template-columns: repeat(auto-fit, minmax(350px, 1fr))"] {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
        }
        
        @media (max-width: 768px) {
          section {
            padding: 60px 20px !important;
          }
          
          section:first-of-type {
            padding: 100px 20px 60px 20px !important;
          }
          
          section:first-of-type h1 {
            font-size: 2rem !important;
          }
          
          section:first-of-type p {
            font-size: 1rem !important;
          }
          
          .container {
            padding: 0 20px !important;
          }
          
          /* Services section - Remove top padding in mobile */
          section:nth-of-type(2) {
            padding: 40px 20px 60px 20px !important;
          }
          
          /* Stats section */
          section:nth-of-type(2) > div > div {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
          
          /* Services grid */
          section:nth-of-type(3) > div > div {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
        }
        
        @media (max-width: 480px) {
          section:first-of-type h1 {
            font-size: 1.75rem !important;
          }
          
          section:nth-of-type(2) > div > div {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <FixedActions />
    </div>
  );
};

export default Sports;